import { Component, DestroyRef, ViewChild, model } from '@angular/core';
import { ConfirmationModalComponent } from "../../../../parts/confirmation-modal/confirmation-modal.component";
import { InfoModalComponent } from "../../../../parts/info-modal/info-modal.component";
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { UpdateUserForm } from '../../../../forms/update-user/update-user.form';
import { UserModel } from '../../../../core/models/data/user.model';
import { ServiceResultModel } from '../../../../core/models/service/service-result.model';
import { GenericMessagingConstants, UsersMessagingConstants } from '../../../../core/constants/messaging.constants';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';
import { BaseFormComponent } from '../../../../core/base-classes/base-form-component';
import { CanComponentDeactivate } from '../../../../core/routing/can-deactivate.guard';
import { DirtyFormRegistryService } from '../../../../core/services/dirty-form-registry.service';

@Component({
  selector: 'ms-update-user',
  imports: [ConfirmationModalComponent, InfoModalComponent, UpdateUserForm, EscCloseDirective],
  templateUrl: './update-user.component.html',
})
export class UpdateUserComponent extends BaseFormComponent implements CanComponentDeactivate {
  @ViewChild(UpdateUserForm) updateUserFormRef!: UpdateUserForm;
  @ViewChild('updateUserConfirmationModalRef') confirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('updateUserSaveConfirmationModalRef') saveConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('updateUserInfoModalRef') infoModalRef!: InfoModalComponent;

  public confirmationConfirmFunction: Function = () => {};
  public confirmationCancelFunction: Function = () => {};

  public user = model<UserModel | undefined>(undefined);

  constructor(
    private bootstrapService: BootstrapService,
    private dirtyFormRegistry: DirtyFormRegistryService,
    private destroyRef: DestroyRef
  ) {
    super();
    this.destroyRef.onDestroy(() => this.dirtyFormRegistry.unregister(this));
  }

  public onCancelRequested(): void {
    this.setConfirmationModalFunctions();

    if (this.updateUserFormRef.isAnyFieldChanged()) {
      this.bootstrapService.manageModal("#updateUserModal", ModalActions.Hide);
      this.confirmationModalRef.modalType.set(ModalTypes.Question);
      this.confirmationModalRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
      this.bootstrapService.manageModal("#updateUserConfirmationModal", ModalActions.Show);
    } else {
      this.bootstrapService.manageModal("#updateUserModal", ModalActions.Hide);
      this.dirtyFormRegistry.unregister(this);
    }
  }

  // No submitSucceeded gating needed: isAnyFieldChanged() is a live comparison against selectedUser(),
  // and onUpdateUserResult() updates the `user` model on success — so the comparison converges to
  // false on its own once that propagates, without leaving a stale-dirty window like a FormGroup would.
  public canDeactivate(): boolean | Promise<boolean> {
    return this.awaitLeaveConfirmation(this.updateUserFormRef.isAnyFieldChanged(), () => {
      this.setConfirmationModalFunctions();
      this.bootstrapService.manageModal("#updateUserModal", ModalActions.Hide);
      this.confirmationModalRef.modalType.set(ModalTypes.Question);
      this.confirmationModalRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
      this.bootstrapService.manageModal("#updateUserConfirmationModal", ModalActions.Show);
    });
  }

  public openUpdateUserModal(): void {
    this.dirtyFormRegistry.register(this);
    this.bootstrapService.manageModal("#updateUserModal", ModalActions.Show);
  }

  public callResetForm(): void {
    this.bootstrapService.manageModal("#updateUserConfirmationModal", ModalActions.Hide);
    this.updateUserFormRef.resetForm();
    this.dirtyFormRegistry.unregister(this);
    this.resolvePendingLeave(true);
  }

  public onUpdateUserResult(response: ServiceResultModel<UserModel>): void {
    if (response.success === true) {
      this.user.set(response.data);
    }
    this.infoModalRef.modalType.set(response.success === true ? ModalTypes.Success : ModalTypes.Failure);
    this.infoModalRef.message.set(response.message);

    this.bootstrapService.manageModal("#updateUserInfoModal", ModalActions.Show);
    this.bootstrapService.manageModal("#updateUserModal", ModalActions.Hide);
    this.dirtyFormRegistry.unregister(this);
  }

  public abortCancel(): void {
    this.bootstrapService.manageModal("#updateUserModal", ModalActions.Show);
    this.bootstrapService.manageModal("#updateUserConfirmationModal", ModalActions.Hide);
    this.resolvePendingLeave(false);
  }

  public setConfirmationModalFunctions() : void {
    this.confirmationConfirmFunction = this.callResetForm;
    this.confirmationCancelFunction = this.abortCancel;
  }

  public onEditConfirmationRequested(): void {
    const selected = this.user();
    const message = UsersMessagingConstants.USERS_EDIT_CONFIRMATION()
      .replace('{firstName}', selected?.firstName ?? '')
      .replace('{lastName}', selected?.lastName ?? '');

    this.saveConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.saveConfirmationModalRef.message.set(message);

    this.bootstrapService.manageModal("#updateUserModal", ModalActions.Hide);
    this.bootstrapService.manageModal("#updateUserSaveConfirmationModal", ModalActions.Show);
  }

  public async editConfirmationModalConfirmed(): Promise<void> {
    this.bootstrapService.manageModal("#updateUserSaveConfirmationModal", ModalActions.Hide);
    await this.updateUserFormRef.updateUser();
  }

  public editConfirmationModalCancelled(): void {
    this.bootstrapService.manageModal("#updateUserSaveConfirmationModal", ModalActions.Hide);
    this.bootstrapService.manageModal("#updateUserModal", ModalActions.Show);
  }
}
